import { AdvertiserPage } from "advertiser/components/AdvertiserPage"
import { AdvertisersPage } from "advertiser/components/AdvertisersPage"
import { MyCampaignsPage } from "campaign/components/MyCampaignsPage"
import { MyDspDealsPage } from "campaign/components/MyDspDealsPage"
import { MyForecastsPage } from "campaign/components/MyForecastsPage"
import { Dashboard } from "dashboard/components/Dashboard"
import { CreateCampaignPage } from "form/components/CreateCampaignPage"
import { CreateDspDealPage } from "form/components/CreateDspDealPage"
import { CreateForecastPage } from "form/components/CreateForecastPage"
import { EditCampaignPage } from "form/components/EditCampaignPage"
import { EditDspDealPage } from "form/components/EditDspDealPage"
import { EditForecastPage } from "form/components/EditForecastPage"
import { SummaryCampaignPage } from "form/components/SummaryCampaignPage"
import { SummaryDspDealPage } from "form/components/SummaryDspDealPage"
import { SummaryForecastPage } from "form/components/SummaryForecastPage"
import { getFormLeavePageMessage } from "form/tools/page"
import { CampaignMediaPage } from "media/components/CampaignMediaPage"
import { MediaApprovalListPage } from "media/components/MediaApprovalListPage"
import { OrdersPage } from "order/components/OrdersPage"
import { NotFoundPage } from "page/components/NotFoundPage"
import {
  ADMIN_UI_CONFIG_PATH,
  ADVERTISER_CREATE_PATH,
  ADVERTISER_ID_PATH_PARAM,
  ADVERTISER_UPDATE_PATH,
  ADVERTISERS_PATH,
  CAMPAIGN_CREATE_PATH,
  CAMPAIGN_EDIT_PATH,
  CAMPAIGN_ID_PATH_PARAM,
  CAMPAIGN_MEDIA_PATH,
  CAMPAIGN_SUMMARY_PATH,
  DASHBOARD_PATH,
  DSP_DEAL_CREATE_PATH,
  DSP_DEAL_EDIT_PATH,
  DSP_DEAL_SUMMARY_PATH,
  FORECAST_CREATE_PATH,
  FORECAST_EDIT_PATH,
  FORECAST_SUMMARY_PATH,
  FORGOT_PASSWORD_PATH,
  HOME_PATH,
  IPage,
  LOGIN_PATH,
  MEDIA_ID_PATH_PARAM,
  MY_CAMPAIGNS_PATH,
  MY_FORECASTS_PATH,
  MY_SSP_DEALS_PATH,
  NOT_FOUND_PATH,
  ORDERS_PATH,
  REPORTS_CREATE_PATH,
  REPORTS_EDIT_PATH,
  REPORTS_ID_PATH_PARAM,
  REPORTS_MY_PATH,
  RESET_PASSWORD_PATH,
  REVENUE_PATH,
  SETTINGS_PAGE_ID_PARAM,
  SETTINGS_PATH,
  SIGNUP_PATH,
  SIGNUP_VERIFICATION_PATH,
  SSP_MEDIA_APPROVALS_PATH
} from "page/model"
import { CreateReportsPage } from "reports/components/CreateReportsPage"
import { MyReportsPage } from "reports/components/MyReportsPage"
import { ReportEditPage } from "reports/components/ReportEditPage"
import { RevenuePage } from "reports/components/RevenuePage"
import { SettingsPage } from "ui/components/main-layout/SettingsPage"
import { AdminUiConfigurationPage } from "ui/configuration/components/AdminUiConfigurationPage"
import { canLeaveUiConfigPage } from "ui/configuration/tools/page"
import { ForgotPasswordPage } from "user/components/ForgotPasswordPage"
import { LoginPage } from "user/components/LoginPage"
import { ResetPasswordPage } from "user/components/ResetPasswordPage"
import { SignUpPage } from "user/components/sign-up/SignUpPage"
import { SignUpVerificationPage } from "user/components/sign-up/SignUpVerificationPage"
import { IUserInfo } from "user/model"
import {
  canAccessDspDeal,
  canAccessForecast,
  canReadCampaigns,
  canSaveCampaigns,
  isSspUserSession
} from "user/tools/permissions"

export const getAllPages = (userInfo?: IUserInfo): IPage[] => {
  let userCanReadCampaigns = true
  let userCanSaveCampaigns = true
  let userCanAccessForecast = true
  let userCanAccessDspDeal = true
  let userIsSspUser = false
  if (userInfo) {
    userIsSspUser = isSspUserSession(userInfo)
    userCanReadCampaigns = canReadCampaigns(userInfo)
    userCanSaveCampaigns = canSaveCampaigns(userInfo)
    userCanAccessForecast = canAccessForecast(userInfo)
    userCanAccessDspDeal = canAccessDspDeal(userInfo)
  }
  return [
    {
      path: LOGIN_PATH,
      jsxElement: LoginPage,
      usedForEverybody: true,
      notLogguedAvailability: true
    },
    {
      path: SIGNUP_PATH,
      jsxElement: SignUpPage,
      notLogguedAvailability: true
    },
    {
      path: SIGNUP_VERIFICATION_PATH,
      jsxElement: SignUpVerificationPage,
      notLogguedAvailability: true
    },
    {
      path: FORGOT_PASSWORD_PATH,
      jsxElement: ForgotPasswordPage,
      notLogguedAvailability: true
    },
    {
      path: RESET_PASSWORD_PATH,
      jsxElement: ResetPasswordPage,
      notLogguedAvailability: true
    },
    {
      path: DASHBOARD_PATH,
      disabled: !userIsSspUser,
      jsxElement: Dashboard
    },
    {
      path: HOME_PATH,
      jsxElement: userIsSspUser ? RevenuePage : userCanReadCampaigns ? MyCampaignsPage : MyReportsPage
    },
    {
      path: HOME_PATH,
      jsxElement: AdminUiConfigurationPage,
      usedForAdminOnly: true
    },
    {
      path: CAMPAIGN_CREATE_PATH,
      jsxElement: CreateCampaignPage,
      disabled: !userCanReadCampaigns || !userCanSaveCampaigns
    },
    {
      path: CAMPAIGN_EDIT_PATH,
      params: [CAMPAIGN_ID_PATH_PARAM],
      jsxElement: EditCampaignPage,
      confirmLeaving: () => getFormLeavePageMessage(),
      disabled: !userCanReadCampaigns || !userCanSaveCampaigns
    },
    {
      path: MY_CAMPAIGNS_PATH,
      jsxElement: MyCampaignsPage,
      disabled: !userCanReadCampaigns
    },
    {
      path: CAMPAIGN_SUMMARY_PATH,
      params: [CAMPAIGN_ID_PATH_PARAM],
      jsxElement: SummaryCampaignPage,
      disabled: !userCanReadCampaigns
    },
    {
      path: ADVERTISERS_PATH,
      jsxElement: AdvertisersPage,
      disabled: !userCanReadCampaigns && !userCanAccessDspDeal
    },
    {
      path: ADVERTISER_CREATE_PATH,
      jsxElement: AdvertiserPage,
      disabled: !userCanReadCampaigns && !userCanAccessDspDeal
    },
    {
      path: ADVERTISER_UPDATE_PATH,
      params: [ADVERTISER_ID_PATH_PARAM],
      jsxElement: AdvertiserPage,
      disabled: !userCanReadCampaigns && !userCanAccessDspDeal
    },
    {
      path: ORDERS_PATH,
      jsxElement: OrdersPage,
      disabled: !userCanReadCampaigns && !userCanAccessDspDeal
    },
    {
      path: FORECAST_CREATE_PATH,
      jsxElement: CreateForecastPage,
      disabled: !userCanAccessForecast
    },
    {
      path: FORECAST_EDIT_PATH,
      params: [CAMPAIGN_ID_PATH_PARAM],
      jsxElement: EditForecastPage,
      confirmLeaving: () => getFormLeavePageMessage(),
      disabled: !userCanAccessForecast
    },
    {
      path: MY_FORECASTS_PATH,
      jsxElement: MyForecastsPage,
      disabled: !userCanAccessForecast
    },
    {
      path: FORECAST_SUMMARY_PATH,
      params: [CAMPAIGN_ID_PATH_PARAM],
      jsxElement: SummaryForecastPage,
      disabled: !userCanAccessForecast
    },
    {
      path: DSP_DEAL_CREATE_PATH,
      jsxElement: CreateDspDealPage,
      disabled: !userCanAccessDspDeal
    },
    {
      path: DSP_DEAL_EDIT_PATH,
      params: [CAMPAIGN_ID_PATH_PARAM],
      jsxElement: EditDspDealPage,
      confirmLeaving: () => getFormLeavePageMessage(),
      disabled: !userCanAccessDspDeal
    },
    {
      path: MY_SSP_DEALS_PATH,
      jsxElement: MyDspDealsPage,
      disabled: !userCanAccessDspDeal
    },
    {
      path: DSP_DEAL_SUMMARY_PATH,
      params: [CAMPAIGN_ID_PATH_PARAM],
      jsxElement: SummaryDspDealPage,
      disabled: !userCanAccessDspDeal
    },
    {
      path: SSP_MEDIA_APPROVALS_PATH,
      jsxElement: MediaApprovalListPage,
      disabled: !userCanAccessDspDeal
    },
    {
      path: REVENUE_PATH,
      jsxElement: RevenuePage,
      disabled: !userCanAccessDspDeal
    },
    {
      path: NOT_FOUND_PATH,
      jsxElement: NotFoundPage,
      usedForEverybody: true
    },
    {
      path: CAMPAIGN_MEDIA_PATH,
      params: [CAMPAIGN_ID_PATH_PARAM, MEDIA_ID_PATH_PARAM],
      jsxElement: CampaignMediaPage
    },
    {
      path: ADMIN_UI_CONFIG_PATH,
      jsxElement: AdminUiConfigurationPage,
      usedForAdminOnly: true,
      confirmLeaving: () => canLeaveUiConfigPage()
    },
    {
      path: REPORTS_CREATE_PATH,
      jsxElement: CreateReportsPage
    },
    {
      path: REPORTS_EDIT_PATH,
      params: [REPORTS_ID_PATH_PARAM],
      jsxElement: ReportEditPage
    },
    {
      path: REPORTS_MY_PATH,
      jsxElement: MyReportsPage
    },
    {
      path: SETTINGS_PATH,
      params: [SETTINGS_PAGE_ID_PARAM],
      jsxElement: SettingsPage
    }
  ]
}
